import { Component, DestroyRef, inject } from '@angular/core';
import { map } from 'rxjs';
import { SidebarTypeEnum } from '../../../../models/common/sidebar-type.enum';
import { CompanyStyleColorSizeGroupRow, CompanyStyleDetails, CompanyStyleDetailsSchema } from 'src/app/components/company/models/company-style-details-schema.type';
import { Template } from 'src/app/components/company/models/template.type';
import { ShotStatusEnum } from '../../../../shared/models/shot-status.enum';
import { TopbarService } from '../../../../shared/services/topbar.service';
import { UtilityFunctions } from '../../../../shared/utilities/functions.models';
import { GraphQLService } from '../../../../services/graph-ql.service';
import { DatabaseQueries, TemplatesQueries, SharedQueries } from '../../../../models/shared/enums/get-query';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AppService } from '../../../../app.service';
import { FilterDialogData } from '../../../../shared/models/types/filter-dialog-data.type';
import { Color } from '../../../../models/common/color.model';
import { ColorFilterService } from 'src/app/shared/services/filters/color-filter.service';
import { SizeGroup } from '../../../../models/common/size-group.model';
import { SizeGroupFilterService } from 'src/app/shared/services/filters/size-group-filter.service';
import { FilterService } from '../../../../shared/services/filter.service';
import { MatDialog } from '@angular/material/dialog';
import { FilterListComponent } from '../../../../shared/components/filter-list/filter-list.component';
import { ActivatedRoute, Router } from '@angular/router';
import { WrapperResultGraphQL } from '../../../../models/shared/types/wrapper-result-graphql.type';
import { ImageService } from '../../../../services/image.service';
import { LoaderService } from '../../../../shared/services/loader.service';
import { Selectable } from 'src/app/shared/models/types/selectable.type';
import { StyleInfo } from '../../models/types/style-info.type';
import { StyleColorShot, StyleColorRow, StyleSizeGroupRow } from '../../models/types/style-color-shot.type';
import { Viewportable } from 'src/app/shared/models/types/viewportable.type';
import { MutationQuery } from 'src/app/models/shared/enums/mutation-query.enum';
import { UtilitiesService } from 'src/app/shared/services/utilities.service';
import { UploadModalComponent } from 'src/app/shared/components/upload-modal/upload-modal.component';
import { CommonService } from 'src/app/services/common.service';
import { ColorSizeShot } from '../../models/types/color-size-shot.type';

@Component({
	standalone: false,
	selector: 'bt-style-info',
	templateUrl: './style-info.component.html',
	styleUrls: ['./style-info.component.scss']
})
export class StyleInfoComponent {
	readonly #graphQLService = inject(GraphQLService);
	readonly #destroyRef = inject(DestroyRef);
	readonly #appService = inject(AppService);
	readonly #dialog = inject(MatDialog);
	readonly #filterService = inject(FilterService);
	// readonly #topbarService = inject(TopbarService);
	readonly #activatedRoute = inject(ActivatedRoute);
	readonly #router = inject(Router);
	readonly #imageService = inject(ImageService);
	readonly #loaderService = inject(LoaderService);
	readonly #utilitiesService = inject(UtilitiesService);
	readonly #commonService = inject(CommonService);
	modelId!: number;

	styleInfo: StyleInfo;
	/** Raggruppamento per colore, con children = size groups (stesso prodotto) */
	colorRows: Array<Viewportable<Selectable<StyleColorRow>>> = [];
	pagination = {
		page: 0,
		size: 8
	}

	loaderId = UtilityFunctions.newGuid();
	shotStatusEnum = ShotStatusEnum;
	actionComments!: string;
	sidebarLoaderId = UtilityFunctions.newGuid();
	imgDetailsElId = UtilityFunctions.newGuid();
	mainContainerElId = UtilityFunctions.newGuid();
	// showGoToTopButton!: boolean;
	// currentSidebarType!: SidebarTypeEnum;
	// sidebarTypeEnum = SidebarTypeEnum;
	// template!: Template;

	filtersList: Array<{ id: 'colors' | 'sizeGroups'; label: string }> = [
		{ id: 'colors', label: 'Colors' },
		{ id: 'sizeGroups', label: 'Size Groups' }
	];

	colors: FilterDialogData<Color> = {
		title: 'Colors',
		selected: [],
		filterService: inject(ColorFilterService),
		variables: { modelId: this.modelId }
	};
	sizeGroups: FilterDialogData<SizeGroup> = {
		title: 'Size Groups',
		selected: [],
		filterService: inject(SizeGroupFilterService)
	};

	constructor() {
		this.queryParamsSubscribe();
		this.modelId = Number(this.#activatedRoute.snapshot.params['modelId']);
		this.colors.variables.modelId = this.modelId;

		this.getCompanyStyleInfo();
		this.getCompanyStyleDetails(0);
	}

	get isAllSelected(): boolean {
		let total = 0;
		let selected = 0;
		this.colorRows.forEach((row) => {
			row.children.forEach((sg) => {
				total++;
				if ((sg as any).selected) selected++;
			});
		});
		return total > 0 && selected === total;
	}

	openDialogFilter(title: string): void {
		const initialValue = [...this[title].selected];
		const dialog = this.#dialog.open(FilterListComponent<any>, {
			data: { title: this[title].title, selected: this[title].selected, filterService: this[title].filterService, variables: this[title].variables },
			height: '70%',
			width: 'auto',
			minWidth: '25%'
		});

		dialog.afterClosed().subscribe({
			next: (res) => {
				if (res) {
					this.applyFilters(title, res);
				} else {
					this[title].selected = initialValue;
				}
			}
		});
	}

	getLabel(filter: 'colors' | 'sizeGroups'): string {
		return this[filter].selected.length
			? `: ${this[filter].selected
				.map((el) => el.displayName)
				.join(', ')
				.slice(0, 20)}`
			: '';
	}

	getFullTitleFilter(filter: 'colors' | 'sizeGroups'): string {
		return this[filter].selected.length ? `${this[filter].selected.map((el) => el.displayName).join(', ')}` : '';
	}

	clearFilter(event: any, filter: 'colors' | 'sizeGroups'): void {
		event.stopPropagation();
		this[filter].selected = [];
		(this[filter] as FilterDialogData<any>).filterService.clearSelection();
		this.#filterService.removeDataFromUrl(filter, () => null);
	}

	applyFilters(filter: string, selected: Array<any>) {
		if (!selected.length) {
			// Rimozione il parametro dall'url
			this.#filterService.removeDataFromUrl(filter, () => null);
		} else {
			this[filter].selected = selected;
			const ids = selected.map((el) => `${el.displayId}`).join('-');
			this.#filterService.setQueryParams({ [filter]: ids });
		}
	}

	onBack() {
		this.#router.navigate(['/company/styles']);
	}

	downloadImages() {
		const shotIds: number[] = [];
		this.colorRows.forEach((row) => {
			row.children.forEach((sg: any) => {
				if (sg.selected && sg.shots) {
					sg.shots.forEach((s: any) => {
						if (s.shotId) shotIds.push(s.shotId);
					});
				}
			});
		});

		this.#imageService
			.downloadImagesByShotIds(shotIds)
			.pipe(takeUntilDestroyed(this.#destroyRef))
			.subscribe({
				next: (res: Blob) => {
					const url = window.URL.createObjectURL(res);
					window.open(url, '_blank');
				},
				error: (err) => {
					console.error(err);
				}
			});
	}

	inViewportAction(ev: any, item: Viewportable<Selectable<StyleColorRow>>) {
		item.isVisible = ev.isVisible;
		if (item.isVisible) {
			const idx = this.colorRows.indexOf(item);
			if (
				idx === this.colorRows.length - 1 &&
				this.colorRows.length / this.pagination.size === this.pagination.page + 1
			) {
				this.getCompanyStyleDetails(this.pagination.page + 1);
			}
		}
	}

	toggleDetail(row: Selectable<StyleColorRow>) {
		row.disabled = !row.disabled;
	}

	toggleColorSelection(colorRow: Selectable<StyleColorRow>, ev: Event) {
		ev.stopPropagation();
		const allSelected = colorRow.children.every((c: any) => c.selected);
		colorRow.children.forEach((c: any) => (c.selected = !allSelected));
	}

	isColorFullySelected(colorRow: Selectable<StyleColorRow>): boolean {
		return colorRow.children.length > 0 && colorRow.children.every((c: any) => c.selected);
	}

	isColorIndeterminate(colorRow: Selectable<StyleColorRow>): boolean {
		const selected = colorRow.children.filter((c: any) => c.selected).length;
		return selected > 0 && selected < colorRow.children.length;
	}

	// onReshoot() {
	// 	this.currentSidebarType = SidebarTypeEnum.customSidebar;
	// }

	reshoot() {
		const text = 'Rejected shot';
		this.actionComments = this.actionComments?.trim() ? text + ': ' + this.actionComments : text;
		// this.companyService.updateShotStatus(this.styleDetails.activeShot.shotId, ShotStatusEnum.Rejected, this.actionComments, this.sidebarLoaderId).subscribe(() => {
		//   this.onExitSidebar();
		//   this.styleDetails.activeShot.stateId = ShotStatusEnum.Rejected;
		// });
	}

	onTemplateInfo(item: CompanyStyleColorSizeGroupRow) {
		// item.showMenu = false;
		// this.styleDetails.tmpRow = item;
		// this.currentSidebarType = SidebarTypeEnum.customSidebar;

		// const filters = {
		// 	companyId: this.#appService.companyId,
		// 	paginationPage: 0,
		// 	paginationSize: 10,
		// 	brandIds: [this.styleDetails.schema.style.brandId],
		// 	categoryIds: [this.styleDetails.schema.style.categoryId]
		// };

		// this.#graphQLService
		// 	.getData<Template>(TemplatesQueries.GET_TEMPLATES, filters)
		// 	.pipe(
		// 		takeUntilDestroyed(this.#destroyRef),
		// 		map((res: any) => res['data']['templates'])
		// 	)
		// 	.subscribe((data: WrapperResultGraphQL<any>) => {
		// 		this.template = data.nodes[0];
		// 	});
	}

	getData() {
		const params = this.#filterService.getDataFromUrl();
		this.getParams(params);
	}

	selectAllStyles() {
		const select = !this.isAllSelected;
		this.colorRows.forEach((row) => {
			row.children.forEach((sg: any) => (sg.selected = select));
		});
	}

	countSelectedStyles(): number {
		let count = 0;
		this.colorRows.forEach((row) => {
			row.children.forEach((sg: any) => {
				if (sg.selected) count++;
			});
		});
		return count;
	}

	setDefaultColor(event: any, colorId: number) {
		event.stopPropagation();
		this.#graphQLService
			.postData(MutationQuery.SET_DEFAULT_COLOR, { companyId: this.#appService.companyId, modelId: this.modelId, colorId: colorId })
			.pipe(takeUntilDestroyed(this.#destroyRef))
			.subscribe({
				next: (res: any) => {
					if (res.data.setDefaultColor.success) {
						this.#utilitiesService.showSnackbar('Default color set successfully');
						this.getCompanyStyleDetails(this.pagination.page);
					}

				},
				error: (err) => this.#utilitiesService.showSnackbar(err)
			});
	}

	openUploadModal(shot: ColorSizeShot) {
		const dialog = this.#dialog.open(UploadModalComponent, {
			data: { single: true }
		});

		dialog.afterClosed()
			.pipe(takeUntilDestroyed(this.#destroyRef))
			.subscribe((res: Array<File>) => {
				if (res) {
					this.uploadFiles(res, [shot]);
				}
			});
	}

	private uploadFiles(files: Array<File>, shots: Array<ColorSizeShot>) {
		const formData = new FormData();
		files.forEach(file => {
			formData.append('files', file);
		});

		formData.append('shots', JSON.stringify(shots));

		this.#commonService.uploadFiles(formData)
			.pipe(takeUntilDestroyed(this.#destroyRef))
			.subscribe((res: Array<string>) => {
				console.log(res);
			});
	}

	private getCompanyStyleInfo() {
		this.#graphQLService
			.getData<StyleInfo>(DatabaseQueries.GET_COMPANY_STYLE_INFO, { companyId: this.#appService.companyId, modelId: this.modelId })
			.pipe(takeUntilDestroyed(this.#destroyRef), map((res: any) => res['data']['companyStyleInfo']['nodes'][0]))
			.subscribe((data: StyleInfo) => {
				this.styleInfo = data;
			});
	}

	private getCompanyStyleDetails(page: number) {
		if (!this.modelId) return;

		this.#loaderService.showLoader(this.loaderId);
		this.pagination.page = page;
		const colorIds = this.#activatedRoute.snapshot.queryParams['colors'] ? this.#activatedRoute.snapshot.queryParams['colors'].split('-').map((el: string) => +el) : [];
		const sizeGroupIds = this.#activatedRoute.snapshot.queryParams['sizeGroups']
			? this.#activatedRoute.snapshot.queryParams['sizeGroups'].split('-').map((el: string) => +el)
			: [];

		const filters = {
			currentUserId: this.#appService.user.userId,
			modelId: this.modelId,
			paginationPage: page,
			paginationSize: this.pagination.size,
			colorIds,
			sizeGroupIds
		};

		this.#graphQLService
			.getData<StyleColorShot>(DatabaseQueries.GET_COMPANY_STYLE_DETAILS, filters)
			.pipe(
				takeUntilDestroyed(this.#destroyRef),
				map((res: any) => res['data']['companyStyleDetails']['nodes'])
			)
			.subscribe({
				next: (data: Array<StyleColorShot>) => {
					const cloneData = structuredClone(data);
					const byColor = new Map<number, { colorId: number; colorName: string; defaultColor?: boolean; groups: Map<string, { sizeGroupId?: number; sizeGroupName?: string; shots: any[] }> }>();
					cloneData.forEach((el: any) => {
						const cId = el.colorId ?? 0;
						if (!byColor.has(cId)) {
							byColor.set(cId, {
								colorId: cId,
								colorName: el.colorName ?? '',
								defaultColor: el.defaultColor,
								groups: new Map()
							});
						}
						const colorEntry = byColor.get(cId)!;
						const sgKey = `${el.sizeGroupId ?? 'sg'}`;
						colorEntry.groups.set(sgKey, {
							sizeGroupId: el.sizeGroupId,
							sizeGroupName: el.sizeGroupName,
							shots: el.shots ?? []
						});
					});

					const newRows: Array<Viewportable<Selectable<StyleColorRow>>> = [];
					byColor.forEach((colorEntry) => {
						const children: Array<Selectable<StyleSizeGroupRow>> = [];
						colorEntry.groups.forEach((g) => {
							children.push({
								...g,
								displayId: colorEntry.colorId * 1000 + (g.sizeGroupId ?? 0),
								displayName: (g.sizeGroupName != null && g.sizeGroupName !== '') ? `${colorEntry.colorName} - ${g.sizeGroupName}` : colorEntry.colorName,
								selected: false,
								disabled: false
							});
						});
						newRows.push({
							colorId: colorEntry.colorId,
							colorName: colorEntry.colorName,
							defaultColor: colorEntry.defaultColor,
							children,
							selected: false,
							disabled: false,
							displayId: colorEntry.colorId,
							displayName: colorEntry.colorName,
							isVisible: false
						});
					});

					if (this.pagination.page === 0) {
						this.colorRows = newRows;
					} else {
						newRows.forEach((newRow) => {
							const existing = this.colorRows.find((r) => r.colorId === newRow.colorId);
							if (existing) {
								newRow.children.forEach((sg: any) => {
									const exists = existing.children.some((c: any) => c.sizeGroupId === sg.sizeGroupId && (c.sizeGroupId != null || sg.sizeGroupId == null));
									if (!exists) existing.children.push(sg);
								});
							} else {
								this.colorRows.push(newRow);
							}
						});
					}
					this.#loaderService.dismissLoader(this.loaderId);
				},
				error: () => {
					this.#loaderService.dismissLoader(this.loaderId);
				}
			});
	}

	private getParams(params: { [key: string]: string }): void {
		if (params['colors']) {
			const ids = params['colors'].split('-').map((el) => +el);
			this.getColors(ids);
		} else {
			this.colors.selected = [];
		}

		if (params['sizeGroups']) {
			const ids = params['sizeGroups'].split('-').map((el) => +el);
			this.getSizeGroups(ids);
		} else {
			this.sizeGroups.selected = [];
		}

		// if (params['search']) this.styleDetails.search = params['search'];
	}

	private getColors(colorIds: Array<number>) {
		const filters = { colorIds };
		this.#graphQLService
			.getData(SharedQueries.GET_COLORS_BY_IDS, filters)
			.pipe(
				takeUntilDestroyed(this.#destroyRef),
				map((res: any) => res['data']['colors'])
			)
			.subscribe((data: WrapperResultGraphQL<any>) => {
				this['colors'].selected = data.nodes.map((el) => ({
					colorId: el.id,
					colorCode: el.ode,
					colorName: el.name,
					fullName: el.finalColorName,
					displayId: el.id!,
					displayName: el.finalColorName,
					disabled: false,
					selected: true
				}));
			});
	}

	private getSizeGroups(sizeGroupIds: Array<number>) {
		const filters = { sizeGroupIds };
		this.#graphQLService
			.getData(SharedQueries.GET_SIZE_GROUPS, filters)
			.pipe(
				takeUntilDestroyed(this.#destroyRef),
				map((res: any) => res['data']['sizeGroups'])
			)
			.subscribe((data: WrapperResultGraphQL<any>) => {
				this['sizeGroups'].selected = data.nodes.map((el) => ({ ...el, displayId: el.id!, displayName: el.name, disabled: false, selected: true }));
			});
	}

	private queryParamsSubscribe() {
		this.#activatedRoute.queryParams.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((data) => {
			this.getData();
			this.getCompanyStyleDetails(0);
		});
	}
}
